import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import TableComponent from 'components/common/TableComponent'
import { formatReset } from './format'

const SectionTitle = ({ title, hint }) => (
  <div className='mb-2'>
    <h4 className='text-base font-semibold text-greenPrimary'>{title}</h4>
    {hint && <p className='text-sm text-gray-500'>{hint}</p>}
  </div>
)

SectionTitle.propTypes = {
  title: PropTypes.string.isRequired,
  hint: PropTypes.string
}

// Highlights a count only when it's evidence (non-zero).
const evidenceCell = value => (
  <span className={Number(value) > 0 ? 'font-semibold text-amber-700' : ''}>{value ?? 0}</span>
)

// A farm's batches without a prefix can be hundreds of ids: cut to keep the other
// columns in view. The full list stays in the tooltip and the exports.
const longTextCell = value => (
  <span className='block max-w-[14rem] truncate' title={value}>{value ?? '-'}</span>
)

const PrefixTables = ({ byPrefix, prefixCollisions, onPrefixClick }) => {
  const { t } = useTranslation()
  const yesNo = value => (value ? t('system.scanRejections.yes') : t('system.scanRejections.no'))

  const prefixTableData = {
    columns: [
      { key: 'farmName', labelKey: 'system.scanRejections.cols.tenant' },
      { key: 'prefix', labelKey: 'system.scanRejections.cols.prefix', cell: value => value || t('system.scanRejections.noPrefix') },
      { key: 'stickerIds', labelKey: 'system.scanRejections.cols.batches', cell: longTextCell },
      { key: 'stickerTypes', labelKey: 'system.scanRejections.cols.stickerType' },
      { key: 'resetTimes', labelKey: 'system.scanRejections.cols.reset', cell: (value, row) => formatReset(value, row.resetEverys) },
      { key: 'isPickSticker', labelKey: 'system.scanRejections.cols.isPickSticker', cell: yesNo },
      { key: 'quantity', labelKey: 'system.scanRejections.cols.quantity' },
      { key: 'accepted', labelKey: 'system.scanRejections.cols.accepted' },
      { key: 'rejections', labelKey: 'system.scanRejections.cols.rejections', orderBy: 'desc' },
      { key: 'boxes', labelKey: 'system.scanRejections.cols.boxes' },
      { key: 'rejectionRate', labelKey: 'system.scanRejections.cols.rejectionRate', cell: value => `${value ?? 0}%` },
      { key: 'batchMismatches', labelKey: 'system.scanRejections.cols.batchMismatches', cell: evidenceCell },
      { key: 'windowBeforeDayStart', labelKey: 'system.scanRejections.cols.windowBeforeDayStart', cell: evidenceCell },
      { key: 'reasons', labelKey: 'system.scanRejections.cols.reasons' },
      { key: 'matchedBy', labelKey: 'system.scanRejections.cols.matchedBy' }
    ],
    rows: byPrefix
  }

  const collisionsTableData = {
    columns: [
      { key: 'farmName', labelKey: 'system.scanRejections.cols.tenant' },
      { key: 'stickerIdA', labelKey: 'system.scanRejections.cols.batchA' },
      { key: 'prefixA', labelKey: 'system.scanRejections.cols.prefixA' },
      { key: 'stickerTypeA', labelKey: 'system.scanRejections.cols.stickerType' },
      { key: 'resetTimeA', labelKey: 'system.scanRejections.cols.reset', cell: (value, row) => formatReset(value, row.resetEveryA) },
      { key: 'quantityA', labelKey: 'system.scanRejections.cols.quantity' },
      { key: 'stickerIdB', labelKey: 'system.scanRejections.cols.batchB' },
      { key: 'prefixB', labelKey: 'system.scanRejections.cols.prefixB' },
      { key: 'stickerTypeB', labelKey: 'system.scanRejections.cols.stickerType' },
      { key: 'resetTimeB', labelKey: 'system.scanRejections.cols.reset', cell: (value, row) => formatReset(value, row.resetEveryB) },
      { key: 'quantityB', labelKey: 'system.scanRejections.cols.quantity' }
    ],
    rows: prefixCollisions
  }

  return (
    <div className='flex flex-col gap-6'>
      <div>
        <SectionTitle
          title={t('system.scanRejections.byPrefixTitle')}
          hint={t('system.scanRejections.byPrefixHint')}
        />
        <TableComponent
          tableData={prefixTableData}
          defaultSortColumn='rejections'
          defaultSortOrder='desc'
          onRowClick={onPrefixClick}
          exportFileName='scan-rejections-by-prefix'
          exportTitle={t('system.scanRejections.byPrefixTitle')}
        />
      </div>
      <div>
        <SectionTitle
          title={t('system.scanRejections.collisionsTitle')}
          hint={t('system.scanRejections.collisionsHint')}
        />
        <TableComponent
          tableData={collisionsTableData}
          exportFileName='sticker-prefix-collisions'
          exportTitle={t('system.scanRejections.collisionsTitle')}
        />
      </div>
    </div>
  )
}

PrefixTables.propTypes = {
  byPrefix: PropTypes.arrayOf(PropTypes.object).isRequired,
  prefixCollisions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onPrefixClick: PropTypes.func.isRequired
}

export default PrefixTables
